import axios from 'axios'

import { Message } from 'element-ui'

const http = axios.create({
	baseURL: process.env.VUE_APP_BASE_API,
	timeout: 1000 * 30,
	withCredentials: true
})

// 用于存储正在进行的刷新token请求
let refreshTokenPromise = null

/**
 * 安全获取token的函数 - 兼容VSCode webview环境
 */
function getToken(key) {
	try {
		// 首先尝试从sessionStorage获取
		let token = sessionStorage.getItem(key);
		if (token) {
			return token;
		}
		
		// 如果在VSCode环境中，尝试从VSCode服务获取
		if (window.vscodeServices && window.vscodeServices.get) {
			try {
				const userStateService = window.vscodeServices.get('IUserStateService');
				if (userStateService && userStateService.currentUser) {
					const user = userStateService.currentUser;
					if (key === 'accessToken' && user.accessToken) {
						return user.accessToken;
					}
					if (key === 'refreshToken' && user.refreshToken) {
						return user.refreshToken;
					}
				}
			} catch (vscodeError) {
				console.warn('[HttpRequest] Failed to get token from VSCode services:', vscodeError);
			}
		}
		
		return null;
	} catch (error) {
		console.warn('[HttpRequest] Failed to get token:', error);
		return null;
	}
}

/**
 * 安全设置token的函数 - 兼容VSCode webview环境
 */
function setToken(key, value) {
	try {
		// 设置到sessionStorage
		sessionStorage.setItem(key, value);
		
		// 如果在VSCode环境中，同步到VSCode服务
		if (window.vscodeServices && window.vscodeServices.get) {
			try {
				const userStateService = window.vscodeServices.get('IUserStateService');
				if (userStateService && userStateService.currentUser) {
					const user = userStateService.currentUser;
					if (key === 'accessToken') {
						user.accessToken = value;
					}
					if (key === 'refreshToken') {
						user.refreshToken = value;
					}
					console.log('[HttpRequest] Token synced to VSCode services');
				}
			} catch (vscodeError) {
				console.warn('[HttpRequest] Failed to sync token to VSCode services:', vscodeError);
			}
		}
	} catch (error) {
		console.error('[HttpRequest] Failed to set token:', error);
	}
}

/**
 * 请求拦截
 */
http.interceptors.request.use(config => {
	let accessToken = getToken("accessToken");
	if (accessToken) {
		config.headers.accessToken = encodeURIComponent(accessToken);
	}
	return config
}, error => {
	return Promise.reject(error)
})

/**
 * 响应拦截
 */
http.interceptors.response.use(async response => {
	if (response.data.code == 200) {
		return response.data.data;
	} else if (response.data.code == 400) {
		location.href = "/";
	} else if (response.data.code == 401) {
		console.log("token失效，尝试重新获取")
		let refreshToken = getToken("refreshToken");
		if (!refreshToken) {
			location.href = "/";
			return Promise.reject(response.data);
		}
		
		// 如果已经有一个正在进行的刷新token请求，等待它完成
		if (refreshTokenPromise) {
			console.log("等待已有的刷新token请求完成");
			try {
				await refreshTokenPromise;
				// 刷新完成后，使用新的token重试请求
				const newAccessToken = getToken("accessToken");
				response.config.headers.accessToken = encodeURIComponent(newAccessToken);
				return http(response.config);
			} catch (error) {
				// 刷新失败，跳转到登录页
				location.href = "/";
				return Promise.reject(error);
			}
		}
		
		// 创建新的刷新token请求
		refreshTokenPromise = (async () => {
			try {
				const data = await http({
					method: 'put',
					url: '/refreshToken',
					headers: {
						refreshToken: refreshToken
					}
				});
				
				// 保存新的token
				setToken("accessToken", data.accessToken);
				setToken("refreshToken", data.refreshToken);
				console.log("token刷新成功");
				
				/* // 同步更新 VSCode 的 token
				if (window.vscodeServices) {
					try {
						const userStateService = window.vscodeServices.get('IUserStateService');
						const currentUser = userStateService.currentUser;
						if (currentUser) {
							// 更新用户信息中的 token
							currentUser.accessToken = data.accessToken;
							// 通知 VSCode 更新用户状态
							await userStateService.login(currentUser, data.accessToken, data.refreshToken);
							console.log("VSCode token 同步更新成功");
						}
					} catch (error) {
						console.error("VSCode token 同步更新失败:", error);
						// 即使同步失败，也不影响正常的请求重试
					}
				}
				
				// 重新初始化 IM 组件
				// 触发事件让 Inbox 组件重新初始化
				const tokenRefreshEvent = new CustomEvent('im:token:refreshed', {
					detail: {
						accessToken: data.accessToken,
						refreshToken: data.refreshToken
					}
				});
				window.dispatchEvent(tokenRefreshEvent);
				console.log("触发 IM 重新初始化事件"); */
				
				return data;
			} catch (error) {
				console.log("刷新token失败，token已失效，需要重新登录");
				/* // 调用 VSCode 的 logout
				if (window.vscodeServices) {
					try {
						const userStateService = window.vscodeServices.get('IUserStateService');
						await userStateService.logout();
						console.log("VSCode logout 调用成功");
					} catch (logoutError) {
						console.error("VSCode logout 调用失败:", logoutError);
					}
				} */
				throw error;
			} finally {
				// 清除promise缓存
				refreshTokenPromise = null;
			}
		})();
		
		// 等待刷新完成
		try {
			await refreshTokenPromise;
			// 使用新的token重试请求
			const newAccessToken = getToken("accessToken");
			response.config.headers.accessToken = encodeURIComponent(newAccessToken);
			return http(response.config);
		} catch (error) {
			// 刷新失败，跳转到登录页
			location.href = "/";
			return Promise.reject(error);
		}
	} else {
		Message({
			message: response.data.message,
			type: 'error',
			duration: 1500,
			customClass: 'element-error-message-zindex'
		})
		return Promise.reject(response.data)
	}
}, async error => {
	// 处理网络错误或无响应的情况
	if (!error.response) {
		Message({
			message: '网络连接失败',
			type: 'error',
			duration: 1500,
			customClass: 'element-error-message-zindex'
		})
		return Promise.reject(error)
	}
	
	switch (error.response.status) {
		case 400:
			Message({
				message: error.response.data,
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
		case 401: {
			console.log("HTTP 401错误，尝试刷新token")
			const refreshToken = getToken("refreshToken");
			if (!refreshToken) {
				location.href = "/";
				return Promise.reject(error);
			}
			
			// 如果已经有一个正在进行的刷新token请求，等待它完成
			if (refreshTokenPromise) {
				console.log("等待已有的刷新token请求完成");
				try {
					await refreshTokenPromise;
					// 刷新完成后，使用新的token重试请求
					const newAccessToken = getToken("accessToken");
					error.config.headers.accessToken = encodeURIComponent(newAccessToken);
					return http(error.config);
				} catch (refreshError) {
					// 刷新失败，跳转到登录页
					location.href = "/";
					return Promise.reject(refreshError);
				}
			}
			
			// 创建新的刷新token请求
			refreshTokenPromise = (async () => {
				try {
					const data = await http({
						method: 'put',
						url: '/refreshToken',
						headers: {
							refreshToken: refreshToken
						}
					});
					
					// 保存新的token
					setToken("accessToken", data.accessToken);
					setToken("refreshToken", data.refreshToken);
					console.log("token刷新成功");
					
					return data;
				} catch (refreshError) {
					console.log("刷新token失败，token已失效，需要重新登录");
					throw refreshError;
				} finally {
					// 清除promise缓存
					refreshTokenPromise = null;
				}
			})();
			
			// 等待刷新完成
			try {
				await refreshTokenPromise;
				// 使用新的token重试请求
				const newAccessToken = getToken("accessToken");
				error.config.headers.accessToken = encodeURIComponent(newAccessToken);
				return http(error.config);
			} catch (refreshError) {
				// 刷新失败，跳转到登录页
				location.href = "/";
				return Promise.reject(refreshError);
			}
		}
		// 401处理中已经return，不需要break
		case 405:
			Message({
				message: 'http请求方式有误',
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
		case 404:
		case 500:
			Message({
				message: '服务器出了点小差，请稍后再试',
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
		case 501:
			Message({
				message: '服务器不支持当前请求所需要的某个功能',
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
	}

	return Promise.reject(error)
})


export default http
